import { useRef, useState, type MouseEvent, type PointerEvent, type ReactNode } from 'react';

/** Decorative feedback only. Activation remains the native click/submit event. */
export function useRipple(disabled = false): {
  ripples: ReactNode;
  pointerDown: (event: PointerEvent<HTMLElement>) => void;
  click: (event: MouseEvent<HTMLElement>) => void;
} {
  const sequence = useRef(0);
  const [waves, setWaves] = useState<{ id: number; x: number; y: number; size: number }[]>([]);
  const add = (target: HTMLElement, x?: number, y?: number): void => {
    if (disabled || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    const bounds = target.getBoundingClientRect();
    const size = Math.hypot(bounds.width, bounds.height) * 2;
    const wave = {
      id: sequence.current++,
      x: x === undefined ? bounds.width / 2 : x - bounds.left,
      y: y === undefined ? bounds.height / 2 : y - bounds.top,
      size,
    };
    setWaves((current) => [...current.slice(-3), wave]);
  };
  return {
    pointerDown: (event) => {
      if (event.button === 0) add(event.currentTarget, event.clientX, event.clientY);
    },
    click: (event) => {
      if (event.detail === 0) add(event.currentTarget);
    },
    ripples: (
      <span className="ui-ripples" aria-hidden="true">
        {waves.map((wave) => (
          <span
            key={wave.id}
            className="ui-ripple"
            style={{ width: wave.size, height: wave.size, left: wave.x, top: wave.y }}
            onAnimationEnd={() =>
              setWaves((current) => current.filter((item) => item.id !== wave.id))
            }
          />
        ))}
      </span>
    ),
  };
}
